import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ViewAsBannerParams, ViewAsBannerState } from 'src/app/models/viewAs.model';
import { ToastService } from 'src/app/services/toast.service';
import { ViewAsService } from 'src/app/services/view-as.service';
import { VIEW_AS_MY_DATA_NOTE } from 'src/app/shared/constants/viewAs.constants';

const ANOTHER_ROLE = 'another role';
const OWN_ORG_LEVELS = 'your own organization levels';
const ORG_LEVEL_COUNT = '{{count}} organization levels';

@Component({
    standalone: false,
    selector: 'app-view-as-banner',
    templateUrl: './view-as-banner.component.html',
    styleUrls: ['./view-as-banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewAsBannerComponent implements OnInit, OnDestroy {
    isActive = false;
    isExiting = false;
    bannerParams: ViewAsBannerParams | null = null;
    readonly limitationNote = VIEW_AS_MY_DATA_NOTE;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly viewAsService: ViewAsService,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) { }

    // Starting, switching or ending a preview always reloads the app, so the state read here holds
    // for the page's lifetime; it comes from the stored access token, so it survives a reload.
    ngOnInit(): void {
        const state = this.viewAsService.getBannerState();
        this.isActive = !!state;
        if (!state) {
            return;
        }
        const count = state.selection.organizationLevelIds.length;
        this.translateService.stream([ANOTHER_ROLE, OWN_ORG_LEVELS, ORG_LEVEL_COUNT], { count })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (translations: Record<string, string>) => {
                    this.bannerParams = this.toParams(state, translations);
                    this.changeDetectorRef.markForCheck();
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    exit(): void {
        if (this.isExiting) {
            return;
        }
        this.isExiting = true;
        this.viewAsService.stop().pipe(
            finalize(() => {
                this.isExiting = false;
                this.changeDetectorRef.markForCheck();
            }),
        ).subscribe({
            error: (error: unknown) => this.toastService.showError(error),
        });
    }

    private toParams(state: ViewAsBannerState, translations: Record<string, string>): ViewAsBannerParams {
        const orgLevelNames = state.labels?.organizationLevelNames ?? [];
        let orgLevels = translations[OWN_ORG_LEVELS];
        if (orgLevelNames.length) {
            orgLevels = orgLevelNames.join(', ');
        } else if (state.selection.organizationLevelIds.length) {
            orgLevels = translations[ORG_LEVEL_COUNT];
        }
        return {
            role: state.labels?.roleName || translations[ANOTHER_ROLE],
            orgLevels,
        };
    }
}
